import type { TeilnahmeStatus, TerminEinladung } from "../../../services/graphMailService";
import { toGraphDate } from "../../../utils/graphDatum";
import { graphAllDayDate } from "../../calendar/allDay";

/**
 * Termin-Einladungen auf dem Handy (Outlook iOS): die kompakte Zeile
 * „Heute, 11:30 (30 Min.)", Konflikte mit dem eigenen Kalender und der Stand
 * der Antwort. Rein, damit testbar.
 */

type Zeitraum = Pick<TerminEinladung, "start" | "ende" | "ganztaegig">;

/** Beginn und Ende als `Date` (Graph liefert UTC ohne Suffix). */
export function terminIntervall(e: Zeitraum): { start: Date; ende: Date } | null {
    if (!e.start) return null;
    if (e.ganztaegig) {
        const start = graphAllDayDate(e.start.dateTime);
        const ende = e.ende ? graphAllDayDate(e.ende.dateTime) : new Date(start.getTime() + 86_400_000);
        return { start, ende };
    }
    const start = toGraphDate(e.start.dateTime);
    if (Number.isNaN(start.getTime())) return null;
    const ende = e.ende ? toGraphDate(e.ende.dateTime) : start;
    return { start, ende: Number.isNaN(ende.getTime()) ? start : ende };
}

/** „30 Min.", „1 Std.", „1 Std. 30 Min." */
export function dauerText(minuten: number): string {
    if (!Number.isFinite(minuten) || minuten <= 0) return "";
    const h = Math.floor(minuten / 60);
    const m = Math.round(minuten % 60);
    if (h === 0) return `${m} Min.`;
    return m === 0 ? `${h} Std.` : `${h} Std. ${m} Min.`;
}

const gleicherTag = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

/** „Heute", „Morgen", sonst „Do., 09.10.". */
export function tagText(d: Date, jetzt: Date = new Date()): string {
    if (gleicherTag(d, jetzt)) return "Heute";
    const morgen = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate() + 1);
    if (gleicherTag(d, morgen)) return "Morgen";
    return d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });
}

/** „Heute, 11:30 (30 Min.)" bzw. „Morgen (ganztägig)". */
export function terminKurzText(e: Zeitraum, jetzt: Date = new Date()): string {
    const iv = terminIntervall(e);
    if (!iv) return "";
    if (e.ganztaegig) return `${tagText(iv.start, jetzt)} (ganztägig)`;
    const uhr = iv.start.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
    const dauer = dauerText((iv.ende.getTime() - iv.start.getTime()) / 60_000);
    return `${tagText(iv.start, jetzt)}, ${uhr}${dauer ? ` (${dauer})` : ""}`;
}

/** Ein Termin aus dem eigenen Kalender (Ausschnitt von `CalendarEvent`). */
export interface KalenderTermin {
    id: string;
    iCalUId?: string | null;
    subject: string;
    start: { dateTime: string };
    end: { dateTime: string };
    isAllDay: boolean;
    showAs?: string | null;
}

/** Ist dieser Kalendereintrag die Einladung selbst (Graph legt sie vorläufig an)? */
function istDieEinladung(t: KalenderTermin, e: Pick<TerminEinladung, "eventId" | "betreff">, start: Date): boolean {
    if (t.id === e.eventId) return true;
    return t.subject === e.betreff && toGraphDate(t.start.dateTime).getTime() === start.getTime();
}

/**
 * Termine, die sich mit der Einladung überschneiden — ohne die Einladung
 * selbst, ohne Ganztägiges und ohne „frei".
 */
export function konflikte(
    e: Pick<TerminEinladung, "eventId" | "betreff" | "start" | "ende" | "ganztaegig">,
    termine: readonly KalenderTermin[],
): KalenderTermin[] {
    const iv = terminIntervall(e);
    if (!iv || e.ganztaegig) return [];
    return termine.filter((t) => {
        if (t.isAllDay || t.showAs === "free" || istDieEinladung(t, e, iv.start)) return false;
        const s = toGraphDate(t.start.dateTime).getTime();
        const en = toGraphDate(t.end.dateTime).getTime();
        return s < iv.ende.getTime() && en > iv.start.getTime();
    });
}

export function konfliktText(anzahl: number): string {
    if (anzahl === 0) return "Keine Konflikte";
    return anzahl === 1 ? "1 Konflikt" : `${anzahl} Konflikte`;
}

/**
 * Tagesausschnitt für das Antwort-Sheet: die Termine des Tages, die bis zu
 * zwei Stunden vor bzw. nach der Einladung liegen, nach Beginn sortiert.
 */
export function tagesAusschnitt(
    e: Pick<TerminEinladung, "start" | "ende" | "ganztaegig">,
    termine: readonly KalenderTermin[],
): KalenderTermin[] {
    const iv = terminIntervall(e);
    if (!iv) return [];
    const von = iv.start.getTime() - 2 * 3_600_000;
    const bis = iv.ende.getTime() + 2 * 3_600_000;
    return termine
        .filter((t) => !t.isAllDay)
        .filter((t) => toGraphDate(t.start.dateTime).getTime() < bis && toGraphDate(t.end.dateTime).getTime() > von)
        .sort((a, b) => toGraphDate(a.start.dateTime).getTime() - toGraphDate(b.start.dateTime).getTime());
}

/** Der bisherige Stand als kurzes Wort für den Knopf; `null` = noch offen. */
export function antwortKurz(status: TeilnahmeStatus): string | null {
    if (status === "accepted") return "Zugesagt";
    if (status === "tentativelyAccepted") return "Vielleicht";
    if (status === "declined") return "Abgesagt";
    return null;
}
